import React, { useEffect, useState } from 'react';
import { StyledDiv, StyledSpan } from '../../../Theme/CommonStyled';
import Button from '../Button';
import Icon from '../Icon';
import { useTranslation } from 'react-i18next';
import {
  StyledModal,
  StyledModalOverlay,
  StyledModalContent,
  StyledModalHeader,
  StyledModalBody,
  StyledModalFooter,
  StyledInstallButton,
  StyledCloseButton,
  StyledBrandInfo
} from './style';

interface PWAInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandName?: string;
  logoUrl?: string;
}

// Type for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallModal: React.FC<PWAInstallModalProps> = ({
  isOpen,
  onClose,
  brandName,
  logoUrl
}) => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setCanInstall(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setCanInstall(false);
      onClose();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [onClose]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setCanInstall(false);
        onClose();
      }
    }
  };

  const handleClose = () => {
    onClose();
    // Store in localStorage to not show again for this session
    localStorage.setItem('pwa_install_dismissed', 'true');
  };

  if (!isOpen || !canInstall) return null;

  return (
    <StyledModal>
      <StyledModalOverlay onClick={handleClose} />
      <StyledModalContent>
        <StyledModalHeader>
          <StyledDiv display="flex" alignItems="center" gap={3}>
            <Icon name="download" fontSize={24} color="primaryGreen" />
            <StyledSpan fontSize={6} fontWeight={6}>
              {t('pwa.installTitle')}
            </StyledSpan>
          </StyledDiv>
          <StyledCloseButton onClick={handleClose}>
            <Icon name="close" fontSize={20} />
          </StyledCloseButton>
        </StyledModalHeader>

        <StyledModalBody>
          <StyledBrandInfo>
            {logoUrl && (
              <StyledDiv
                width="60px"
                height="60px"
                borderRadius="50%"
                backgroundImage={`url(${logoUrl})`}
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                backgroundSize="cover"
                mb={3}
              />
            )}
            <StyledDiv textAlign="center" mb={4}>
              <StyledSpan fontSize={5} fontWeight={6} display="block" mb={2}>
                {brandName || t('pwa.defaultBrandName')}
              </StyledSpan>
              <StyledSpan fontSize={3} color="secondaryText">
                {t('pwa.installDescription')}
              </StyledSpan>
            </StyledDiv>
          </StyledBrandInfo>

          <StyledDiv bg="secondaryBackground" p={4} borderRadius="lg" mb={4}>
            <StyledDiv display="flex" alignItems="center" gap={3} mb={3}>
              <Icon name="check_circle" fontSize={16} color="primaryGreen" />
              <StyledSpan fontSize={3}>{t('pwa.benefit1')}</StyledSpan>
            </StyledDiv>
            <StyledDiv display="flex" alignItems="center" gap={3} mb={3}>
              <Icon name="check_circle" fontSize={16} color="primaryGreen" />
              <StyledSpan fontSize={3}>{t('pwa.benefit2')}</StyledSpan>
            </StyledDiv>
            <StyledDiv display="flex" alignItems="center" gap={3}>
              <Icon name="check_circle" fontSize={16} color="primaryGreen" />
              <StyledSpan fontSize={3}>{t('pwa.benefit3')}</StyledSpan>
            </StyledDiv>
          </StyledDiv>
        </StyledModalBody>

        <StyledModalFooter>
          <StyledDiv display="flex" gap={3}>
            <Button variant="outline" onClick={handleClose} width="50%">
              {t('pwa.later')}
            </Button>
            <StyledInstallButton onClick={handleInstall} width="50%">
              <Icon name="download" fontSize={16} mr={2} />
              {t('pwa.install')}
            </StyledInstallButton>
          </StyledDiv>
        </StyledModalFooter>
      </StyledModalContent>
    </StyledModal>
  );
};

export default PWAInstallModal;
